import { useCallback, useEffect, useState } from 'react';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Content, Header, Page } from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';

// Kibana Discover URL with audit data view and last 24h no auto-refresh.
const KIBANA_DISCOVER_URL =
  '/kibana/app/discover#/?_g=(filters:!(),refreshInterval:(pause:!t,value:60000),time:(from:now-24h,to:now))&_a=(columns:!(),dataSource:(dataViewId:\'code4ev-audit\',type:dataView),filters:!(),interval:auto,query:(language:kuery,query:\'\'),sort:!(!(\'@timestamp\',desc)))';

export function AuditLogPage() {
  const identityApi = useApi(identityApiRef);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [iframeSrc, setIframeSrc] = useState<string>('');

  const grantInfraSession = useCallback(async (): Promise<boolean> => {
    try {
      const { token } = await identityApi.getCredentials();
      const resp = await fetch('/api/infra/grant-session', {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      return resp.ok;
    } catch {
      return false;
    }
  }, [identityApi]);

  const prepareKibanaAccess = useCallback(async () => {
    setLoading(true);
    setError(false);
    const ok = await grantInfraSession();
    if (!ok) {
      setLoading(false);
      setError(true);
      return;
    }
    setIframeSrc(KIBANA_DISCOVER_URL);
  }, [grantInfraSession]);

  useEffect(() => {
    void prepareKibanaAccess();
  }, [prepareKibanaAccess]);

  return (
    <Page themeId="tool">
      <Header
        title="Audit Log"
        subtitle="Powered by Kibana / Elasticsearch"
      >
        <Button
          variant="outlined"
          size="small"
          endIcon={<OpenInNewIcon />}
          onClick={async () => {
            const ok = await grantInfraSession();
            if (ok) {
              window.open(KIBANA_DISCOVER_URL, '_blank');
            } else {
              setError(true);
            }
          }}
          style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
        >
          Open Kibana
        </Button>
      </Header>
      <Content noPadding>
        {loading && !error && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, gap: 12 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="textSecondary">Loading Kibana…</Typography>
          </div>
        )}
        {error && (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <Typography color="error" gutterBottom>
              Kibana is not available yet.
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              You may not have owner access yet, or your infra session could not be granted.
            </Typography>
            <Button variant="outlined" size="small" onClick={() => { void prepareKibanaAccess(); }}>
              Retry
            </Button>
          </div>
        )}
        <iframe
          src={iframeSrc}
          title="Kibana Audit Log"
          style={{
            width: '100%',
            height: 'calc(100vh - 120px)',
            border: 'none',
            display: error ? 'none' : 'block',
          }}
          onLoad={() => setLoading(false)}
          onError={() => { setLoading(false); setError(true); }}
        />
      </Content>
    </Page>
  );
}
